import IntlMessageFormat from "intl-messageformat";
import { Settings } from "luxon";
import { watch } from "vue";
import type { CompileError, MessageCompiler, MessageContext } from "vue-i18n";
import { createI18n } from "vue-i18n";

import { logError } from "../utils/logError";
import { logUserInteraction } from "../utils/logUtils";
import { formattingLocale } from "./formattingLocale";
import enMessages from "./locales/en.json";

export const DEFAULT_LOCALE: string = "en";

// Project Elrond's key convention (BRA-1044) ships messages as ICU MessageFormat, not vue-i18n's
// own `|`-pipe plural syntax, so translators on the translation platform and engineers reading
// en.json use the same plural/select/interpolation syntax. vue-i18n has no built-in ICU support,
// so messages are compiled with intl-messageformat instead of vue-i18n's default compiler.
export const messageCompiler: MessageCompiler = (message, { key, locale, onError }) => {
    if (typeof message !== "string") {
        onError?.(new Error(`${key}: pre-compiled message resources are not supported`) as CompileError);
        return () => key;
    }
    const formatter = new IntlMessageFormat(message, locale);
    return (ctx: MessageContext) => formatter.format(ctx.values) as string;
};

export const TRANSLATION_MISSING_EVENT = "translation_missing";

// vue-i18n calls `missing` once per locale it tries, so a key absent from "fr" that English
// then covers reports once as "fr", and a key absent everywhere reports as "fr" and then "en".
// Posted to coach_api, which turns it into a Datadog metric (BRA-1179): nothing else surfaces a
// translation gap in production. Once per locale+key per page load, so one broken screen
// re-rendering does not emit thousands of points; a plain module-level Set is enough for the
// same reason as reportedLocaleFallbacks below.
const reportedMissingTranslations = new Set<string>();

function reportMissingTranslation(locale: string, key: string): void {
    const dedupeKey = `${locale}:${key}`;
    if (reportedMissingTranslations.has(dedupeKey)) {
        return;
    }
    reportedMissingTranslations.add(dedupeKey);
    void logUserInteraction(TRANSLATION_MISSING_EVENT, { locale, key });
}

// Dev: vue-i18n's default behaviour (render the raw key) so a missing translation is obvious
// immediately while building a feature. Production: a key missing even from the English
// fallback is a real bug, but a raw translation key must never reach a user's screen - log it
// and render nothing instead. Both report the miss to the server first.
export function createMissingHandler(isDev: boolean) {
    return (locale: string, key: string): string | undefined => {
        reportMissingTranslation(locale, key);
        if (isDev) {
            return undefined;
        }
        console.error(`[i18n] missing translation: locale="${locale}" key="${key}"`);
        return "";
    };
}

export const i18n = createI18n({
    legacy: false,
    locale: DEFAULT_LOCALE,
    // vue-i18n's own internal default fallback locale is "en-US", not our source locale, so this
    // must be set explicitly - it is not enough to rely on the library default. Once set,
    // vue-i18n's registered fallback algorithm (fallbackWithLocaleChain, not something this file
    // implements) already steps a region variant through its base language first, e.g.
    // "pt-BR" -> "pt" -> "en" rather than straight to "en" - see index.spec.ts.
    fallbackLocale: DEFAULT_LOCALE,
    messageCompiler,
    missing: createMissingHandler(import.meta.env.DEV),
    missingWarn: import.meta.env.DEV,
    fallbackWarn: import.meta.env.DEV,
    messages: {
        [DEFAULT_LOCALE]: enMessages,
    },
});

// luxon formats in whatever language vue-i18n is actually showing - including the
// fallback-to-English paths below - so dates and labels never disagree (BRA-1047). Left unset,
// luxon follows the browser's language, which is not the user's choice; only its region is kept
// (see formattingLocale). This default is not
// reactive; BaseDate / formatDate read the locale ref directly so a computed re-renders on switch.
watch(
    i18n.global.locale,
    (locale) => {
        Settings.defaultLocale = formattingLocale(locale);
    },
    { immediate: true },
);

const loadedLocales = new Set<string>([DEFAULT_LOCALE]);

type LocaleModule = { default: Record<string, unknown> };

// Lazy (not eager), so each matched file - every locale except English, which is already
// statically imported above - is still its own content-hashed chunk (AC7). Built from the glob
// rather than a plain dynamic import so a locale with no shipped catalogue is detected by a
// missing map entry instead of a dynamic-import helper throwing at runtime.
const localeLoaders = import.meta.glob<LocaleModule>("./locales/*.json");

// Dev already gets a loud console.warn per call; production has no equivalent signal, so a
// catalogue that silently falls back to English would otherwise go unnoticed. Reported once per
// locale per page load (this module is instantiated once per real browser page load, so a plain
// module-level Set is enough) to avoid flooding on repeated attempts at the same broken locale.
// No user data in the report - just the locale code and why it fell back.
const reportedLocaleFallbacks = new Set<string>();

function reportLocaleFallback(locale: string, reason: "missing_catalogue" | "load_failed"): void {
    if (reportedLocaleFallbacks.has(locale)) {
        return;
    }
    reportedLocaleFallbacks.add(locale);
    logError(new Error(`i18n: falling back to "${DEFAULT_LOCALE}" for locale "${locale}" (${reason})`), {
        tags: { feature: "i18n_locale_fallback", locale, reason },
    });
}

let currentRequestId = 0;

type CatalogueLoad = "loaded" | "missing_catalogue" | "load_failed";

async function loadCatalogue(locale: string, loaders: Record<string, () => Promise<LocaleModule>>): Promise<CatalogueLoad> {
    if (loadedLocales.has(locale)) {
        return "loaded";
    }
    const load = loaders[`./locales/${locale}.json`];
    if (!load) {
        return "missing_catalogue";
    }
    try {
        const module = await load();
        i18n.global.setLocaleMessage(locale, module.default);
        loadedLocales.add(locale);
        return "loaded";
    } catch (error) {
        if (import.meta.env.DEV) {
            console.warn(`[i18n] failed to load catalogue for locale "${locale}"`, error);
        }
        return "load_failed";
    }
}

// Makes a locale's messages available to `t(key, values, { locale })` without switching to it,
// for UI that shows a line in a language the page is not in. Resolves false when there is no
// catalogue to load; unlike `setLocale` that is not reported, since callers expect it.
export async function loadLocaleMessages(locale: string, loaders: Record<string, () => Promise<LocaleModule>> = localeLoaders): Promise<boolean> {
    return (await loadCatalogue(locale, loaders)) === "loaded";
}

// Loads (or reuses) a locale's catalogue, then switches to it - in that order, so a component
// never renders a raw key or a half-applied locale while the chunk is in flight (BRA-1044 AC9).
// A locale with no shipped catalogue, or whose chunk fails to load, falls back to English
// silently rather than switching - there is nothing to switch to, and the alternative is an
// unhandled rejection with the UI stuck mid-navigation. `loaders` defaults to the real glob map;
// the parameter exists so tests can inject a controllable one instead of depending on real
// locale files on disk.
//
// Calls can overlap (e.g. a user switches locale twice before the first chunk resolves), so every
// write to the active locale is gated on this call still being the latest one requested - an
// in-flight call that loses the race must not undo a newer selection, whether it eventually
// succeeds or fails.
export async function setLocale(locale: string, loaders: Record<string, () => Promise<LocaleModule>> = localeLoaders): Promise<void> {
    const requestId = ++currentRequestId;
    const isLatest = () => requestId === currentRequestId;

    if (loadedLocales.has(locale)) {
        if (isLatest()) {
            i18n.global.locale.value = locale;
        }
        return;
    }

    const result = await loadCatalogue(locale, loaders);
    if (result !== "loaded") {
        if (import.meta.env.DEV) {
            console.warn(`[i18n] no usable catalogue for locale "${locale}" (${result}) - staying on "${DEFAULT_LOCALE}"`);
        } else {
            reportLocaleFallback(locale, result);
        }
    }
    if (isLatest()) {
        i18n.global.locale.value = result === "loaded" ? locale : DEFAULT_LOCALE;
    }
}
